import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface ShopsFiltersState {
  mic_channel_id: string[];
  active: string[];
  fulfillment: string[];
  default_payment_method_id: string[];
}

interface UseShopsFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

/**
 * Hook para gestionar los filtros simplificados de Shops.
 * Convierte el estado UI a FilterConditionOutput[] para la API.
 */
export const useShopsFilters = ({ initialConditions = [], onChange }: UseShopsFiltersProps) => {
  const [filters, setFilters] = useState<ShopsFiltersState>(() => parseConditionsToState(initialConditions));

  const convertStateToConditions = useCallback((state: ShopsFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    if (state.mic_channel_id.length > 0) {
      conditions.push({
        column: 'mic_channel_id',
        action: 'in',
        value: state.mic_channel_id,
      });
    }
    if (state.active.length > 0) {
      conditions.push({
        column: 'active',
        action: 'in',
        value: state.active,
      });
    }
    if (state.fulfillment.length > 0) {
      conditions.push({
        column: 'fulfillment',
        action: 'in',
        value: state.fulfillment,
      });
    }
    if (state.default_payment_method_id.length > 0) {
      conditions.push({
        column: 'default_payment_method_id',
        action: 'in',
        value: state.default_payment_method_id,
      });
    }

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<ShopsFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  const handleChannelChange = useCallback(
    (values: string[]) => updateFilters({ mic_channel_id: values }),
    [updateFilters],
  );
  const handleActiveChange = useCallback((values: string[]) => updateFilters({ active: values }), [updateFilters]);
  const handleFulfillmentChange = useCallback(
    (values: string[]) => updateFilters({ fulfillment: values }),
    [updateFilters],
  );
  const handlePaymentMethodChange = useCallback(
    (values: string[]) => updateFilters({ default_payment_method_id: values }),
    [updateFilters],
  );

  const clearAllFilters = useCallback(() => {
    const emptyState: ShopsFiltersState = {
      mic_channel_id: [],
      active: [],
      fulfillment: [],
      default_payment_method_id: [],
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  useEffect(() => {
    setFilters(parseConditionsToState(initialConditions));
  }, [initialConditions]);

  return {
    filters,
    handleChannelChange,
    handleActiveChange,
    handleFulfillmentChange,
    handlePaymentMethodChange,
    clearAllFilters,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): ShopsFiltersState {
  const state: ShopsFiltersState = {
    mic_channel_id: [],
    active: [],
    fulfillment: [],
    default_payment_method_id: [],
  };

  const getArrayValue = (column: keyof ShopsFiltersState): string[] => {
    const condition = conditions.find((c) => c.column === column && c.action === 'in');
    if (!condition?.value) return [];
    return Array.isArray(condition.value) ? condition.value : [String(condition.value)];
  };

  state.mic_channel_id = getArrayValue('mic_channel_id');
  state.active = getArrayValue('active');
  state.fulfillment = getArrayValue('fulfillment');
  state.default_payment_method_id = getArrayValue('default_payment_method_id');

  return state;
}
